import React from 'react';
import { Plus, Trash2, ChevronLeft } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import GroupAvatar from './GroupAvatar';
import MemberStack from './MemberStack';
import { CrewMarker, UnreadDot } from './PaneChrome';
import type { ChatSession } from './types';
import { TYPE } from '../../constants/typeScale';

interface Props {
  sessions: ChatSession[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  /** Delete this crew chat for good (the host confirms first — the row control never deletes on its own). */
  onDelete: (id: string) => void;
  /** True while the first load of conversations is in flight (show a spinner, not "empty"). */
  loading?: boolean;
  /** Ids of chats with unseen messages — render an unread dot + bolder title. */
  unreadIds?: Set<string>;
  /** Mobile only (FR4) — leave the secondary chat list and return to the Studio launchpad. */
  onBackToStudio?: () => void;
  /**
   * Which edge the list is docked to, so its divider faces the canvas. 'left' = the mobile list surface;
   * 'right' = level 1 of the workstation's right pane (RF3-WORKSTATION slice C).
   */
  side?: 'left' | 'right';
  /** The open production's crew chat — captioned so it reads as this production's people, not just a chat. */
  crewChatId?: string | null;
  /** A one-line teaching/empty note above the list (e.g. "no crew recruited yet"). */
  notice?: React.ReactNode;
  footer?: React.ReactNode;
}

const ConversationList: React.FC<Props> = ({ sessions, selectedId, onSelect, onNew, onDelete, loading, unreadIds, onBackToStudio, side = 'left', crewChatId, notice, footer }) => (
  /* In the RIGHT pane the border (and the fold/resize controls) belong to the pane itself — drawing our own
     here too would double the line where the two meet. On the left/mobile we are still the whole surface. */
  <aside className={`w-full h-full flex flex-col ${side === 'right' ? '' : 'border-r border-secondary-200 dark:border-secondary-600'} bg-white dark:bg-black`}>
    {/* FR4: chats are secondary on mobile — an explicit way back to the launchpad (the creator's home). */}
    {onBackToStudio && (
      <button type="button" onClick={onBackToStudio}
        className={`flex items-center gap-1.5 px-3 pt-3 ${TYPE.control} text-secondary-500 hover:text-secondary-900 dark:text-secondary-400 dark:hover:text-white md:hidden`}>
        <ChevronLeft className="h-4 w-4 shrink-0" /> Studio
      </button>
    )}
    <div className="p-3 flex items-center gap-2">
      <button
        onClick={onNew}
        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 ${TYPE.control} font-medium hover:bg-black dark:hover:bg-secondary-100 transition-colors`}
      >
        <Plus className="w-4 h-4" />
        New crew
      </button>
      {/* NO COLLAPSE BUTTON HERE: the pane owns its own toggle now (PaneChrome), identical on both sides.
          A bespoke one inside a child list is how the two panes ended up with different-sized icons. */}
    </div>

    {notice && (
      <p className={`mx-3 mb-2 rounded-lg bg-secondary-50 px-3 py-2 ${TYPE.control} leading-relaxed text-secondary-500 dark:bg-secondary-900 dark:text-secondary-400`}>
        {notice}
      </p>
    )}

    <div className="flex-1 overflow-y-auto px-2 pb-2">
      {loading && sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12" data-testid="conversations-loading">
          <LoadingSpinner size="md" color="secondary" />
          <p className={`${TYPE.control} text-secondary-400`}>Loading your crews…</p>
        </div>
      ) : sessions.length === 0 ? (
        <p className={`px-3 py-6 ${TYPE.control} text-secondary-400 text-center`}>No crews yet — assemble one to start a production.</p>
      ) : (
        sessions.map((s) => {
          const unread = !!unreadIds?.has(s.id);
          // The crew of the production under edit: marked on the LEFT, and shown as the FACES the creator
          // recruited rather than a group badge — "which of these groups is helping me right now" has to be
          // answerable at a glance, and by WHO is in it (owner 2026-08-22).
          const isCrew = s.id === crewChatId;
          return (
          <div
            key={s.id}
            data-testid={`chat-row-${s.id}`}
            className={`group relative flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
              s.id === selectedId
                ? 'bg-secondary-100 dark:bg-secondary-800'
                : 'hover:bg-secondary-50 dark:hover:bg-secondary-900'
            }`}
            onClick={() => onSelect(s.id)}
          >
            {isCrew && <CrewMarker />}
            {isCrew && s.members.length > 0
              ? <MemberStack members={s.members} conversationId={s.id} />
              : <GroupAvatar name={s.title} logoUrl={s.logoUrl} className={`w-8 h-8 ${TYPE.control}`} />}
            <div className="min-w-0 flex-1">
              <p className={`${TYPE.control} truncate text-secondary-900 dark:text-white ${unread ? 'font-semibold' : 'font-medium'}`}>{s.title}</p>
              <p className={`${TYPE.meta} truncate ${unread ? 'text-secondary-600 dark:text-secondary-200' : 'text-secondary-400'}`}>
                {isCrew
                  ? 'This production’s crew'
                  : s.messages.length ? s.messages[s.messages.length - 1].content : 'Say hello to your crew'}
              </p>
            </div>
            {/* Unread dot (monochrome) — hidden on hover so the row's control takes its place. */}
            {unread && <UnreadDot className="group-hover:hidden" />}
            {/* DELETE, NOT ARCHIVE (owner 2026-08-23). Archiving moved a chat to a settings page almost nobody
                visits, so a row the creator wanted gone stayed half alive somewhere else. The bin says what it
                does; the host names what is destroyed before anything happens. */}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
              className={`text-secondary-400 hover:text-secondary-700 dark:hover:text-white transition-all focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400 rounded ${unread ? 'hidden group-hover:block focus:block' : 'opacity-0 group-hover:opacity-100'}`}
              aria-label={`Delete ${s.title}`} title="Delete chat"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          );
        })
      )}
    </div>

    {footer && (
      <div className="border-t border-secondary-200 dark:border-secondary-600 p-2">{footer}</div>
    )}
  </aside>
);

export default ConversationList;
